/**
 * Migrates v1 scenarios (content[] per scene) to v2 flat format (1 block = 1 scene).
 */

import type { Scene, Scenario, ChoiceOption, PhoneChatReplyOption, PhoneMailTriageReactionStep } from '../types';
import { normalizeHighlightInEmail, type HighlightInEmailInput } from './phone-mail-triage';
import type {
  V1Scenario,
  V1Scene,
  V1ContentItem,
  V1ChoiceOption,
  V1PhoneChatReplyOption,
  V1TextOverlayOption,
  V1PhoneMailTriageAction,
} from './legacy-v1-types';
import { migrateScenarioToConverge } from '../components/scenario-editor/migrate-scenario-converge';
import {
  buildChapterScriptLayout,
  type ChapterFlatBlock,
  type ChapterScriptSegment,
} from '../components/scenario-editor/inline-choice-model';
import { buildChapterGroups } from '../components/scenario-editor/inline-editor-model';

function sceneIdForBlock(block: ChapterFlatBlock): string {
  if (block.contentIndex === 0) return block.sceneId;
  return `${block.sceneId}__b${block.contentIndex}`;
}

function pickVisuals(v1Scene: V1Scene | undefined, contentIndex: number): Pick<Scene, 'background' | 'elements'> {
  if (!v1Scene || contentIndex > 0) return {};
  const out: Pick<Scene, 'background' | 'elements'> = {};
  if (v1Scene.background) out.background = v1Scene.background;
  if (v1Scene.elements?.length) out.elements = v1Scene.elements;
  return out;
}

function convertPhoneReplyOption(opt: V1PhoneChatReplyOption): PhoneChatReplyOption {
  const next: PhoneChatReplyOption = {
    text: opt.text,
    contactReply: opt.contactReply,
  };
  if (opt.gotoScene && !opt.replyOptions?.length) {
    if (!opt.gotoScene.endsWith('--converge')) {
      next.goto = opt.gotoScene;
    }
  } else if (opt.gotoScene && opt.gotoScene.endsWith('--converge')) {
    // implicit convergence after phone — omit goto
  } else if (opt.gotoScene) {
    next.goto = opt.gotoScene;
  }
  if (opt.replyOptions?.length) {
    next.replyOptions = opt.replyOptions.map(convertPhoneReplyOption);
  }
  return next;
}

function convertOverlayOption(opt: V1TextOverlayOption) {
  return {
    text: opt.text,
    condition: opt.condition,
    actions: opt.actions,
    closeOverlay: opt.closeOverlay,
    goto: opt.gotoScene?.replace('__MAIN_MENU__', '__MAIN_MENU__'),
  };
}

function chapterEndContinueText(
  continueText?: string,
  options?: V1TextOverlayOption[]
): string | undefined {
  if (continueText?.trim()) return continueText.trim();
  const converted = options?.map(convertOverlayOption) ?? [];
  const legacy = converted.find(
    (opt) => opt.goto !== '__MAIN_MENU__' && opt.goto !== '__MENU__'
  );
  return legacy?.text?.trim() || undefined;
}

function convertHighlightInEmail(
  items?: V1PhoneMailTriageAction['highlightInEmail']
) {
  return normalizeHighlightInEmail(items as HighlightInEmailInput[] | undefined);
}

function convertReactionSteps(
  action: V1PhoneMailTriageAction
): PhoneMailTriageReactionStep[] | undefined {
  if (action.reactionSteps?.length) {
    return action.reactionSteps
      .filter((step) => step.reactions?.length)
      .map((step) => ({
        reactions: step.reactions,
        highlightInEmail: convertHighlightInEmail(step.highlightInEmail),
      }));
  }
  if (!action.reactions?.length) return undefined;
  return [{
    reactions: action.reactions,
    highlightInEmail: convertHighlightInEmail(action.highlightInEmail),
  }];
}

function convertTriageAction(action: V1PhoneMailTriageAction) {
  const reactionSteps = convertReactionSteps(action);
  return {
    label: action.label,
    actions: action.actions,
    reactionSteps,
    pedaShields: action.pedaShields,
    goto: action.gotoScene,
  };
}

function v1ItemToV2Scene(
  item: V1ContentItem,
  id: string,
  v1Scene: V1Scene | undefined,
  contentIndex: number
): Scene | null {
  const visuals = pickVisuals(v1Scene, contentIndex);

  switch (item.type) {
    case 'dialogue':
      return {
        id,
        type: 'dialogue',
        ...visuals,
        speaker: item.speaker,
        text: item.text,
        emotion: item.emotion,
        characterId: item.characterId,
        placement: item.placement,
        isInternal: item.isInternal,
        characterMood: item.characterMood,
        condition: item.condition,
        goto: item.gotoScene,
      };
    case 'command':
      return {
        id,
        type: 'command',
        ...visuals,
        command: item.command,
        target: item.target,
        value: item.value,
      };
    case 'choice':
      return {
        id,
        type: 'choice',
        ...visuals,
        prompt: item.prompt,
        phoneMailPreview: item.phoneMailPreview,
        options: [],
      };
    case 'textOverlay':
      if (item.overlayType === 'chapterEnd' && item.chapterEnd) {
        return {
          id,
          type: 'chapterEnd',
          ...visuals,
          ...item.chapterEnd,
          continueText: chapterEndContinueText(item.continueText, item.options),
        };
      }
      return {
        id,
        type: 'textOverlay',
        ...visuals,
        overlayType: item.overlayType || 'letter',
        title: item.title,
        characterName: item.characterName,
        characterIcon: item.characterIcon,
        content: item.content,
        enableTyping: item.enableTyping,
        options: item.options?.map(convertOverlayOption),
        continueText: item.continueText,
        chapterEnd: item.chapterEnd,
      };
    case 'chapterEnd':
      return {
        id,
        type: 'chapterEnd',
        ...visuals,
        eyebrow: item.eyebrow,
        heading: item.heading,
        missionLabel: item.missionLabel,
        missionIndex: item.missionIndex,
        missionTotal: item.missionTotal,
        chapterShieldsMax: item.chapterShieldsMax,
        chapterShieldsCaption: item.chapterShieldsCaption,
        keyIdea: item.keyIdea,
        reflexes: item.reflexes,
        perfNote: item.perfNote,
        perfRows: item.perfRows,
        continueText: chapterEndContinueText(item.continueText, item.options),
      };
    case 'phoneChat':
      return {
        id,
        type: 'phoneChat',
        ...visuals,
        contactName: item.contactName,
        contactAvatar: item.contactAvatar,
        messages: item.messages,
        messageDelay: item.messageDelay,
        typingDelay: item.typingDelay,
        replyOptions: item.replyOptions?.map(convertPhoneReplyOption),
        goto: item.gotoScene && !item.gotoScene.endsWith('--converge') ? item.gotoScene : undefined,
      };
    case 'phoneCall':
      return {
        id,
        type: 'phoneCall',
        ...visuals,
        contactName: item.contactName,
        contactAvatar: item.contactAvatar,
        messages: item.messages,
        messageDelay: item.messageDelay,
        incomingCall: item.incomingCall,
        autoAnswer: item.autoAnswer,
        allowDecline: item.allowDecline,
        ringDurationMs: item.ringDurationMs,
        ringAfterDeclineMs: item.ringAfterDeclineMs,
        replyOptions: item.replyOptions?.map(convertPhoneReplyOption),
        goto: item.gotoScene && !item.gotoScene.endsWith('--converge') ? item.gotoScene : undefined,
      };
    case 'phoneCallBranchLine':
      return {
        id,
        type: 'dialogue',
        ...visuals,
        text: item.playerText,
        isInternal: false,
        goto: item.gotoScene,
      };
    case 'phoneMail':
      return {
        id,
        type: 'phoneMail',
        ...visuals,
        from: item.from,
        subject: item.subject,
        content: item.content,
        goto: item.gotoScene,
        mails: item.mails?.map((mail) => ({
          ...mail,
          triageActions: mail.triageActions.map(convertTriageAction),
        })),
        pedaShieldsMax: item.pedaShieldsMax,
        triageActions: item.triageActions?.map(convertTriageAction),
      };
    case 'phoneMailInteract':
      return {
        id,
        type: 'phoneMailInteract',
        ...visuals,
        from: item.from,
        subject: item.subject,
        preview: item.preview,
        content: item.content,
        unreadCount: item.unreadCount,
        attachmentsNote: item.attachmentsNote,
        goto: item.gotoSceneOnOpen,
      };
    case 'timedOverlay':
      return {
        id,
        type: 'timedOverlay',
        ...visuals,
        message: item.message,
        durationMs: item.durationMs,
        goto: item.gotoScene,
      };
    case 'situationSortGame':
      return {
        id,
        type: 'situationSortGame',
        ...visuals,
        categories: item.categories,
        situations: item.situations,
        instructionText: item.instructionText,
        speakerName: item.speakerName,
        speakerCharacterId: item.speakerCharacterId,
        requireAllCorrect: item.requireAllCorrect,
        goto: item.gotoSceneNext,
      };
    case 'confidenceGain':
      return {
        id,
        type: 'confidenceGain',
        ...visuals,
        characters: item.characters,
        introText: item.introText,
        continueText: item.continueText,
        goto: item.gotoScene,
      };
    default:
      return null;
  }
}

function mergePhoneMailInteractPair(
  menu: V1ContentItem,
  inbox: V1ContentItem,
  id: string,
  v1Scene: V1Scene | undefined
): Scene | null {
  if (menu.type !== 'phoneMailInteract' || inbox.type !== 'phoneMailInteract') return null;
  const visuals = pickVisuals(v1Scene, 0);
  return {
    id,
    type: 'phoneMailInteract',
    ...visuals,
    from: inbox.from ?? menu.from,
    subject: inbox.subject ?? menu.subject,
    preview: inbox.preview ?? menu.preview,
    content: inbox.content,
    unreadCount: menu.unreadCount ?? inbox.unreadCount,
    attachmentsNote: inbox.attachmentsNote,
    goto: inbox.gotoSceneOnOpen,
  };
}

function flatBlocksToV2Scenes(
  blocks: ChapterFlatBlock[],
  v1Scenario: V1Scenario
): Scene[] {
  const scenes: Scene[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i]!;
    const v1Scene = v1Scenario.scenes.find((s) => s.id === block.sceneId);
    const id = sceneIdForBlock(block);

    if (
      block.item.type === 'phoneMailInteract'
      && blocks[i + 1]?.item.type === 'phoneMailInteract'
      && blocks[i + 1]!.sceneId === block.sceneId
    ) {
      const merged = mergePhoneMailInteractPair(block.item, blocks[i + 1]!.item, id, v1Scene);
      if (merged) {
        scenes.push(merged);
        i += 2;
        continue;
      }
    }

    if (v1Scene?.objectInspectMode && block.contentIndex === 0 && block.item.type === 'choice') {
      scenes.push({
        id: v1Scene.id,
        type: 'observation',
        background: v1Scene.background,
        elements: v1Scene.elements,
        exitChoice: {
          text: block.item.options[0]?.text ?? 'Continuer',
          condition: block.item.options[0]?.condition,
        },
      });
      i++;
      continue;
    }

    const converted = v1ItemToV2Scene(block.item, id, v1Scene, block.contentIndex);
    if (converted) scenes.push(converted);
    i++;
  }
  return scenes;
}

function convertChoiceOption(
  v1Opt: V1ChoiceOption,
  branchBlocks: ChapterFlatBlock[],
  v1Scenario: V1Scenario
): ChoiceOption {
  const opt: ChoiceOption = {
    text: v1Opt.text,
    condition: v1Opt.condition,
    actions: v1Opt.actions,
    pedaShields: v1Opt.pedaShields,
    pedaCategory: v1Opt.pedaCategory,
    moodChanges: v1Opt.moodChanges,
  };

  if (v1Opt.gotoScene && !branchBlocks.length) {
    opt.goto = v1Opt.gotoScene;
  } else if (v1Opt.gotoScene && branchBlocks.length) {
    const lastGoto = branchBlocks[branchBlocks.length - 1];
    const lastItem = lastGoto?.item;
    const exitGoto =
      lastItem && 'gotoScene' in lastItem ? (lastItem as { gotoScene?: string }).gotoScene : undefined;
    if (exitGoto && !exitGoto.endsWith('--converge') && exitGoto !== v1Opt.gotoScene) {
      opt.goto = exitGoto;
    }
    opt.scenes = flatBlocksToV2Scenes(
      branchBlocks.map((b) => {
        const copy = { ...b };
        if (copy.item.type === 'dialogue' && copy.item.gotoScene?.endsWith('--converge')) {
          copy.item = { ...copy.item, gotoScene: undefined };
        }
        return copy;
      }),
      v1Scenario
    );
  } else if (branchBlocks.length) {
    opt.scenes = flatBlocksToV2Scenes(branchBlocks, v1Scenario);
  }

  return opt;
}

function segmentToV2Scenes(segment: ChapterScriptSegment, v1Scenario: V1Scenario): Scene[] {
  if (segment.kind === 'block') {
    return flatBlocksToV2Scenes([segment.block], v1Scenario);
  }

  const v1Scene = v1Scenario.scenes.find((s) => s.id === segment.block.sceneId);
  const choiceId = sceneIdForBlock(segment.block);
  const item = segment.block.item;

  if (segment.kind === 'choice') {
    const choiceScene = v1ItemToV2Scene(item, choiceId, v1Scene, segment.block.contentIndex);
    if (!choiceScene || choiceScene.type !== 'choice') return [];
    choiceScene.options = item.type === 'choice'
      ? item.options.map((opt, oi) =>
          convertChoiceOption(opt, segment.branches[oi]?.blocks ?? [], v1Scenario)
        )
      : [];
    return [choiceScene, ...flatBlocksToV2Scenes(segment.convergence, v1Scenario)];
  }

  if (segment.kind === 'phoneCall' || segment.kind === 'phoneChat') {
    const phoneScene = v1ItemToV2Scene(item, choiceId, v1Scene, segment.block.contentIndex);
    if (!phoneScene) return [];
    return [phoneScene, ...flatBlocksToV2Scenes(segment.convergence, v1Scenario)];
  }

  return [];
}

export function collectAllV2SceneIds(scenes: Scene[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: Scene[]) => {
    for (const scene of list) {
      ids.add(scene.id);
      if (scene.type === 'choice') {
        for (const opt of scene.options) {
          if (opt.scenes?.length) walk(opt.scenes);
        }
      }
    }
  };
  walk(scenes);
  return ids;
}

function isAuxiliaryTarget(id: string): boolean {
  return id.endsWith('--converge') || id.includes('__narr');
}

function cleanPhoneReplies(opts: PhoneChatReplyOption[] | undefined): PhoneChatReplyOption[] | undefined {
  if (!opts) return undefined;
  return opts.map((opt) => {
    const next: PhoneChatReplyOption = { ...opt };
    if (next.goto && isAuxiliaryTarget(next.goto)) delete next.goto;
    if (next.replyOptions) next.replyOptions = cleanPhoneReplies(next.replyOptions);
    return next;
  });
}

export function normalizeV2Scenario(scenario: Scenario): Scenario {
  const allIds = collectAllV2SceneIds(scenario.scenes);
  const rootIndex = new Map(scenario.scenes.map((s, i) => [s.id, i]));

  const cleanScene = (scene: Scene, _siblings: Scene[], index: number): Scene => {
    let copy: Scene = { ...scene };
    if (copy.goto) {
      const targetIdx = rootIndex.get(copy.goto);
      const strip =
        isAuxiliaryTarget(copy.goto)
        || !allIds.has(copy.goto)
        || (targetIdx !== undefined && targetIdx === index + 1);
      if (strip) {
        const { goto: _, ...rest } = copy;
        copy = rest as Scene;
      }
    }

    if (copy.type === 'choice') {
      copy = {
        ...copy,
        options: copy.options.map((opt) => {
          const nextOpt = { ...opt };
          if (nextOpt.goto && (isAuxiliaryTarget(nextOpt.goto) || !allIds.has(nextOpt.goto))) {
            delete nextOpt.goto;
          }
          if (nextOpt.scenes?.length) {
            nextOpt.scenes = nextOpt.scenes.map((s, si) => cleanScene(s, nextOpt.scenes!, si));
          }
          return nextOpt;
        }),
      };
    }

    if (copy.type === 'phoneCall' || copy.type === 'phoneChat') {
      copy = { ...copy, replyOptions: cleanPhoneReplies(copy.replyOptions) };
    }

    if (copy.type === 'textOverlay' && copy.options) {
      const options = copy.options.map((opt) => {
        if (opt.goto && isAuxiliaryTarget(opt.goto) && !allIds.has(opt.goto)) {
          const { goto: _, ...rest } = opt;
          return rest;
        }
        return opt;
      });
      copy = { ...copy, options };
    }

    if (copy.type === 'chapterEnd') {
      const { options, ...rest } = copy;
      const continueText = chapterEndContinueText(copy.continueText, options);
      copy = continueText ? { ...rest, continueText } : rest;
    }

    return copy;
  };

  return {
    ...scenario,
    scenes: scenario.scenes.map((scene, index) => cleanScene(scene, scenario.scenes, index)),
  };
}

function isV1Scenario(input: V1Scenario | Scenario): input is V1Scenario {
  const first = input.scenes[0];
  return first != null && 'content' in first && Array.isArray((first as V1Scene).content);
}

function hasV1Content(scene: unknown): scene is V1Scene {
  return (
    scene != null
    && typeof scene === 'object'
    && Array.isArray((scene as V1Scene).content)
  );
}

function ensureDefaultMissionChapter(scenario: Scenario): Scenario {
  const missions = scenario.metadata.missions ?? [];
  const chapters = scenario.metadata.chapters ?? [];
  if (missions.length > 0 || chapters.length > 0) return scenario;

  const startScene = scenario.metadata.startScene || scenario.scenes[0]?.id;
  if (!startScene || !scenario.scenes.some((s) => s.id === startScene)) return scenario;

  const missionId = 'mission-1';
  const chapterId = 'chapitre-1';
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      startScene,
      missions: [{ id: missionId, title: 'Nouvelle mission', order: 1 }],
      chapters: [{ id: chapterId, title: 'Nouveau chapitre', startScene, missionId }],
    },
  };
}

/** Removes deprecated mission.startScene from metadata (derived from chapters at runtime). */
export function stripMissionStartScenes(scenario: Scenario): Scenario {
  const missions = scenario.metadata.missions;
  if (!missions?.length) return scenario;
  if (!missions.some((m) => m.startScene !== undefined)) return scenario;

  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      missions: missions.map(({ startScene: _removed, ...rest }) => rest),
    },
  };
}

/**
 * Ensures a scenario loaded in the studio is in v2 flat format.
 * - Full v1 scenario → complete migration.
 * - Mixed scenario (e.g. a v1 skeleton scene alongside v2 scenes added in the
 *   editor) → converts only the v1-style scenes, item by item, in place.
 * - Already v2 → returned unchanged.
 * - Bare skeleton (no mission/chapter) → wraps start scene in default mission + chapter.
 */
export function ensureV2Scenario(input: V1Scenario | Scenario): Scenario {
  const scenes = input.scenes as unknown[];
  const v1Count = scenes.filter(hasV1Content).length;

  let scenario: Scenario;
  if (v1Count === 0) {
    scenario = input as Scenario;
  } else if (v1Count === scenes.length) {
    scenario = migrateV1ToV2(input as V1Scenario);
  } else {
    const converted: Scene[] = [];
    for (const raw of scenes) {
      if (!hasV1Content(raw)) {
        converted.push(raw as Scene);
        continue;
      }
      const v1Scene = raw;
      v1Scene.content.forEach((item, i) => {
        const id = i === 0 ? v1Scene.id : `${v1Scene.id}__b${i}`;
        const scene = v1ItemToV2Scene(item, id, v1Scene, i);
        if (scene) converted.push(scene);
      });
      if (v1Scene.content.length === 0) {
        // Preserve the scene id so goto/startScene references keep working.
        converted.push({
          id: v1Scene.id,
          type: 'dialogue',
          text: '',
          isInternal: true,
          background: v1Scene.background || undefined,
          elements: v1Scene.elements?.length ? v1Scene.elements : undefined,
        });
      }
    }

    scenario = normalizeV2Scenario({
      metadata: { ...(input.metadata as Scenario['metadata']), version: '2.0.0' },
      items: input.items,
      scenes: converted,
    });
  }

  return stripMissionStartScenes(ensureDefaultMissionChapter(scenario));
}

export function migrateV1ToV2(v1Input: V1Scenario | Scenario): Scenario {
  if (!isV1Scenario(v1Input)) {
    return normalizeV2Scenario(v1Input);
  }
  const converged = migrateScenarioToConverge(structuredClone(v1Input));
  const v2Scenes: Scene[] = [];

  const groups = buildChapterGroups(converged as unknown as Scenario);
  for (const group of groups) {
    const layout = buildChapterScriptLayout(converged, group.scenes as unknown as V1Scene[]);
    for (const segment of layout) {
      v2Scenes.push(...segmentToV2Scenes(segment, converged));
    }
  }

  return normalizeV2Scenario({
    metadata: { ...v1Input.metadata, version: '2.0.0' },
    items: v1Input.items,
    scenes: v2Scenes,
  });
}

export function auditV2Migration(v2: Scenario): string[] {
  const issues: string[] = [];
  const ids = new Set(v2.scenes.map((s) => s.id));

  const walk = (scenes: Scene[]) => {
    for (const scene of scenes) {
      if (scene.goto && !ids.has(scene.goto) && scene.goto !== '__MAIN_MENU__' && scene.goto !== '__MENU__') {
        issues.push(`Missing goto target ${scene.goto} from ${scene.id}`);
      }
      if (scene.type === 'choice') {
        for (const opt of scene.options) {
          if (opt.goto && !ids.has(opt.goto) && opt.goto !== '__MAIN_MENU__') {
            issues.push(`Missing choice goto ${opt.goto} from ${scene.id}`);
          }
          if (opt.scenes?.length) walk(opt.scenes);
        }
      }
    }
  };
  walk(v2.scenes);

  for (const ch of v2.metadata.chapters ?? []) {
    if (!ids.has(ch.startScene)) {
      issues.push(`Chapter startScene missing: ${ch.startScene}`);
    }
  }
  if (!ids.has(v2.metadata.startScene)) {
    issues.push(`metadata.startScene missing: ${v2.metadata.startScene}`);
  }

  return issues;
}
